<script setup>
    import { computed } from 'vue';
    import ScoreAchievement from '@/components/result/ai-literacy/ScoreAchievement.vue';  
    import AchievementTable from '@/components/result/ai-literacy/AchievementTable.vue'; 

    const props = defineProps({
        studentData: Object
    });

    // 학교급별 성취수준 범위 정의
    const getScoreRange = (schulType, level) => {
        const ranges = {
            'E': { // 초등학교
                4: { min: 20.5, max: 26 },
                3: { min: 15.5, max: 20 },
                2: { min: 10, max: 15 },
                1: { min: 0, max: 9.5 }
            },
            'M': { // 중학교
                4: { min: 24, max: 28 },
                3: { min: 18, max: 23 },
                2: { min: 10, max: 17 },
                1: { min: 0, max: 9 }
            }
        };
        return ranges[schulType] && ranges[schulType][level] ? ranges[schulType][level] : { min: 0, max: 0 };
    };

    // ScoreAchievement에 전달할 데이터
    const scoreData = computed(() => {
        if (!props.studentData) return null;
        
        const schulType = props.studentData.schul_type; // 'E' or 'M'
        const achievementLevel = props.studentData.achievement_level; // 1, 2, 3, 4
        const totalScore = props.studentData.total_score; // 총점
        const range = getScoreRange(schulType, achievementLevel);
        
        console.log('📊 ScoreAchievement 데이터:', {
            schulType,
            achievementLevel,
            totalScore,
            range,
            studentName: props.studentData.name
        });
        
        return {
            schulType,
            achievementLevel,
            totalScore,
            scoreRange: range
        };
    });

    // AchievementTable에 전달할 데이터
    const achievementData = computed(() => {
        if (!props.studentData) return null;
        
        const schulType = props.studentData.schul_type; // 'E' or 'M'
        const level = props.studentData.achievement_level; // 1, 2, 3, 4
        
        // 학교급별, 레벨별 피드백 데이터
        const feedbackData = {
            'E': {
                4: "ㆍAI 원리 및 데이터 분석을 깊이 이해하고 AI의 결과물을 비판적으로 평가할 수 있다.\nㆍAI 도구의 장단점을 평가하여 문제 해결과 창작에 창의적으로 적용할 수 있다.\nㆍAI 안전 및 윤리 규칙을 이해하고 사회적 영향을 조사하거나 책임 있는 사용을 적극 실천할 수 있다.\nㆍ인간과 AI의 인지 과정을 비교 분석하며 지속 가능한 문제 정의에 주체적으로 참여할 수 있다.",
                3: "ㆍ기계학습을 체험하고 AI 사례를 구별하며 생성형 AI의 종류와 활용 사례를 설명할 수 있다.\nㆍAI 도구 활용 문제 해결 과정을 탐색하고 AI 콘텐츠 제작을 체험하며 장단점을 구별할 수 있다.\nㆍAI의 안전한 사용을 실천하고 개인정보 보호의 중요성을 설명하며 AI의 이로움과 해로움을 구별할 수 있다.\nㆍAI가 잘하는 일을 발견하고 AI와 협업하는 자세를 인식하며 지속 가능한 AI 활용 사례를 탐색할 수 있다.",
                2: "ㆍ생활 속 AI의 원리와 기계학습의 간단한 개념을 탐색할 수 있다.\nㆍAI 도구의 활용 사례를 탐색하고 가장 기본적인 창작 사례를 찾아 설명할 수 있다.\nㆍ안전한 AI 사용을 위한 기본 규칙과 개인정보 보호의 중요성을 단순하게 설명할 수 있다.\nㆍ지속 가능한 사회를 위한 AI의 활용 사례를 탐색하고 AI가 잘하는 일을 구체적으로 발견할 수 있다.",
                1: "ㆍAI 개념에 대한 이해와 AI인 것을 구별하는 능력을 키울 수 있다.\nㆍAI 도구 활용의 필요성을 인식하고 학습 및 창작 도구 사용 경험을 쌓을 수 있다.\nㆍ안전한 AI를 위한 기본 규칙과 개인정보 보호의 중요성에 대한 인식을 확립할 수 있다.\nㆍAI의 역할과 인간과의 협업 개념에 대한 호기심과 탐구 의지를 키울 수 있다."
            },
            'M': {
                4: "ㆍ간단한 기계학습 모델을 직접 구현할 수 있고, 일상생활과 학습 상황에서 적절한 AI·디지털 도구를 선택하여 문제를 효과적으로 해결할 수 있다.\nㆍAI·디지털 도구 사용 과정에서 발생할 수 있는 윤리적 문제를 식별하고 예방 및 해결 방안을 탐색할 수 있다.\nㆍ지속 가능한 사회를 위해 인공지능을 활용하여 창의적 문제해결 방안을 구안하고 적용할 수 있으며, AI·디지털 도구 활용 전반에서 책임감 있고 주체적인 성숙한 태도를 갖추고 있다",
                3: "ㆍ기계학습의 개념과 종류, 주요 원리를 설명하고, 생성형 인공지능을 다른 인공지능과 비교하여 그 차이를 설명할 수 있다.\nㆍ일상생활과 학습상황에서 AI·디지털 도구를 활용하여 문제를 해결하며, 인공지능의 사회적 영향력과 윤리적 쟁점을 다양한 관점에서 탐색할 수 있다.\nㆍ지속 가능한 사회를 위해 문제를 정의하고 인공지능을 활용한 해결 방안을 구상할 수 있다.\nㆍ인공지능과의 협업 과정에서 인간의 주체적인 역할을 탐색할 수 있다.",
                2: "ㆍ기계학습의 기본 개념과 주요원리, 생성형 인공지능의 기본 개념을 설명할 수 있다.\nㆍAI·디지털 도구를 문제 해결과 창작 활동에 활용하여 문자·그림·영상 등의 결과물을 제작하고, 사용 과정에서 발생할 수 있는 개인정보 보호와 저작권 등 등의 윤리적 이슈를 설명할 수 있다.\nㆍ지속 가능한 발전을 위해 인공지능으로 해결할 수 있는 문제를 정의하고 간단한 해결 방안을 탐색할 수 있다.\nㆍ인공지능을 협업의 대상으로 인식하고 다양한 협업을 시도할 수 있다. ",
                1: "ㆍ인공지능에 대한 기본 이해를 바탕으로 예측·분석·추천·생성 등 인공지능이 하는 일을 구분하는 능력을 키울수 있다.\nㆍ일상생활과 학습 상황에서 문제해결을 위해 필요한 AI·디지털 도구를 탐색하고 적용해 볼 수 있다.\nㆍ딥페이크나 가짜 뉴스와 같은 인공지능 기술 오남용의 위험성을 이해하고, 인공지능 윤리 원칙의 중요성을 인식할 수 있다.\nㆍ인공지능과 협업을 시도하고, 이 과정에서 인간이 잘하는 일과 인공지능이 잘하는 일을 발견할 수 있다."
            }
        };

        const feedback = feedbackData[schulType] && feedbackData[schulType][level] 
            ? feedbackData[schulType][level] 
            : "";

        return {
            schul_lev: schulType.toLowerCase(), // 'e' or 'm'
            level,
            feedback
        };
    });
</script>
<template>
    <div class="report-inner">   
        <div class="card-wrap">
            <ScoreAchievement 
                v-if="scoreData"
                :schulType="scoreData.schulType"
                :achievementLevel="scoreData.achievementLevel"
                :totalScore="scoreData.totalScore"
                :scoreRange="scoreData.scoreRange"
                :studentData="props.studentData"
            />
            <AchievementTable 
                v-if="achievementData"
                :isStudent="true" 
                :studentLevel="achievementData.level"
                :achievementData="achievementData"
                :schulType="achievementData.schul_lev"
            />     
        </div>
    </div>
</template>